import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { calculateBarHeight, formatChartValue, formatCurrencyValue, getChartColorClass } from 'src/app/utils/format.utils';
import { CommonModule } from '@angular/common';

export interface GroupedBarChartItem {
  value: number;
  label?: string; // Label opcional para o item individual
}

export interface GroupedBarChartGroup {
  label: string;
  items: GroupedBarChartItem[];
}

@Component({
  selector: 'app-grouped-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grouped-bar-chart.component.html',
  styleUrls: ['./grouped-bar-chart.component.scss']
})
export class GroupedBarChartComponent implements OnInit, OnChanges {
  @Input() data: GroupedBarChartGroup[] = [];
  @Input() title: string = '';
  @Input() legendItems: string[] = [];
  @Input() showLegend: boolean = true;
  @Input() formatValues: boolean = true;
  @Input() currencyFormat: boolean = false; // Novo parâmetro para formatação monetária
  @Input() integerFormat: boolean = false; // Parâmetro para forçar valores como inteiros
  
  private maxValue: number = 100;
  public gridValues: number[] = [];

  constructor() { }

  ngOnInit(): void {
    this.calculateMaxValue();
    this.calculateGridValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.calculateMaxValue();
      this.calculateGridValues();
    }
  }

  /**
   * Calcula o valor máximo entre todos os itens do gráfico
   * Usado como referência para o cálculo da altura das barras
   */
  private calculateMaxValue(): void {
    if (!this.data || this.data.length === 0) {
      this.maxValue = 100;
      return;
    }

    // Extrai todos os valores de todos os grupos
    const allValues: number[] = [];
    this.data.forEach(group => {
      if (group.items && group.items.length > 0) {
        group.items.forEach(item => {
          if (item && typeof item.value === 'number' && !isNaN(item.value)) {
            allValues.push(item.value);
          }
        });
      }
    });
    
    // Calcula o valor máximo
    let calculatedMax = 0;
    if (allValues.length > 0) {
      calculatedMax = Math.max(...allValues);
    }
    
    // Se o máximo for zero ou muito pequeno, define um valor padrão
    this.maxValue = calculatedMax <= 0 ? 100 : calculatedMax;
  }

  /**
   * Calcula os valores para as linhas do grid
   * Divide o valor máximo em 5 partes para criar valores para o grid
   * com valores arredondados para múltiplos de 10
   */
  private calculateGridValues(): void {
    this.gridValues = [];
    const steps = 5;
    
    // Adicionamos 5% ao valor máximo para dar um pequeno espaço no topo do gráfico
    let adjustedMaxValue = this.maxValue * 1.05;
    
    // Arredondar para o próximo múltiplo de 10 mais alto
    adjustedMaxValue = Math.ceil(adjustedMaxValue / 10) * 10;
    
    // Calcular o valor de cada passo (garantindo que seja múltiplo de 10)
    const stepValue = Math.ceil(adjustedMaxValue / steps / 10) * 10;
    
    // Recalcular o valor máximo ajustado baseado no tamanho do passo
    adjustedMaxValue = stepValue * steps;
    
    // Gerar os valores da grade (em ordem decrescente)
    for (let i = steps; i >= 0; i--) {
      const value = stepValue * i;
      this.gridValues.push(value);
    }
  }
  
  /**
   * Formata o valor para exibição no grid
   */
  formatGridValue(value: number): string {
    if (this.currencyFormat) {
      // Para valores monetários
      if (value >= 1000000) {
        const millions = value / 1000000;
        return 'R$ ' + millions.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }) + 'M';
      }
      
      if (value >= 1000) {
        const thousands = value / 1000;
        return 'R$ ' + thousands.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1
        }) + 'K';
      }
      
      // Para valores menores que 1000
      return 'R$ ' + Math.round(value).toLocaleString('pt-BR');
    }
    
    // Para valores normais, exibir apenas o inteiro
    return Math.round(value).toString();
  }

  /**
   * Obtém a altura da barra com base no valor
   * Garante que mesmo valores pequenos sejam visíveis
   */
  getBarHeight(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '10px'; // Altura mínima para barras sem valor
    }
    
    // Recalcula o maxValue com base nos mesmos ajustes do grid
    let adjustedMaxValue = this.maxValue * 1.05;
    adjustedMaxValue = Math.ceil(adjustedMaxValue / 10) * 10;
    const stepValue = Math.ceil(adjustedMaxValue / 5 / 10) * 10;
    adjustedMaxValue = stepValue * 5;
    
    // Usa o valor máximo ajustado para calcular a altura
    return calculateBarHeight(value, adjustedMaxValue);
  }

  /**
   * Formata o valor para exibição
   */
  formatValue(value: number): string {
    if (!this.formatValues) {
      return value.toString();
    }
    
    // Se a formatação monetária estiver ativada, usa o formato de moeda
    if (this.currencyFormat) {
      return formatCurrencyValue(value);
    }
    
    // Se a formatação de inteiros estiver ativada, arredonda para inteiro
    if (this.integerFormat) {
      return Math.round(value).toString();
    }
    
    // Caso contrário, usa a formatação padrão
    return formatChartValue(value);
  }

  /**
   * Obtém a classe de cor com base no índice
   */
  getColorClass(index: number): string {
    return getChartColorClass(index);
  }
} 